import React, { Component } from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import cn from 'classnames';

import { browserHistory } from '../../browserHistory';
import services, { events, logInfo } from '../../services/services';
import { serializeMessageFull } from '../../services/serializers';
import { setDocumentTitleBase } from '../../actions/actions';
import { detectPlatform, exposeComponentToGlobalScope, parseDocumentTitle } from '../../utils/utilsGlobal';
import {
  Header,
  Alerts,
  PageChat,
  PageSchedule,
  PageAnalytics,
  PageProfile,
  PageIssues,
  PageNotFound,
  PageLogin,
  VersionWatcher,
  LoadingScreen,
  SchedulerReasonModal,
} from '../../components';

import styles from './App.scss';


const mapStateToProps = state => {
  return {
    isLoggedIn: state.isLoggedIn,
    currentRoom: state.currentRoom,
    currentCompany: state.currentCompany,
    currentOperator: state.currentOperator,
    newVersionAvailable: state.newVersionAvailable,
    workHoursSchedulerIsWorking: state.workHoursSchedulerIsWorking,
    documentTitleBase: state.documentTitleBase,
  };
};

const mapDispatchToProps = {
  setDocumentTitleBase,
};

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
export class App extends Component {

  state = {
    isLoading: true,
    isAppMuted: false,
    isEssentialDataMissing: false,
    isSchedulerEnabled: false,
    isSchedulerReasonModalOpen: false,
  };

  componentDidMount(){
    this.checkPlatform();

    services.userWatcher.initialize();
    services.userWatcher.on(events.USER_SET_SUCCESS, this.initializeSubscriptions);
    services.userWatcher.on(events.USER_LOGGED_IN, this.initializeGraphQLClients);

    services.messageSubscription.on(events.MESSAGES_RECEIVE, this.onMessageReceive);
    services.totalUnreadCounter.on(events.TOTAL_UNREAD_COUNTER_CHANGE, this.onUnreadCountChange);
    services.pushNotifications.on(events.PUSH_NOTIFICATIONS_SYNC_NEW_MESSAGE_WITH_APP, this.onPushNotificationReceive);
    services.pushNotifications.on(events.PUSH_NOTIFICATIONS_FORCE_NAVIGATE, this.onPushNotificationClick);

    services.pwa.on(events.PWA_REGISTERED_SERVICE_WORKER, serviceWorkerRegistration => {
      services.openedTabsWatcher.initialize();
      services.pushNotifications.passServiceWorkerRegistration(serviceWorkerRegistration);
    });
    services.soundNotifications.on(events.SOUND_NOTIFICATIONS_TOGGLE_MUTE, isAppMuted => {
      this.setState({ isAppMuted });
    });
    services.workHoursScheduler.on(events.WORK_HOURS_SCHEDULER_IS_WORKING_UPDATE, isWorking => {
      this.setState({ isSchedulerEnabled: isWorking });
    });
    this.setState({
      isAppMuted: services.soundNotifications.isMuted,
      isSchedulerEnabled: services.workHoursScheduler.isWorking,
    });
    exposeComponentToGlobalScope(this);
  };

  initializeSubscriptions = () => {
    const { currentOperator, currentCompany, setDocumentTitleBase, documentTitleBase } = this.props;
    if (!documentTitleBase) {
      setDocumentTitleBase(currentCompany.name);
    }
    this.setState({ isLoading: false });

    services.messageSubscription.subscribe(currentCompany.id);
    services.workHoursScheduler.initialize({ currentCompany, currentOperator });
    services.totalUnreadCounter.initialize({ currentCompany });
  };

  initializeGraphQLClients = (token) => {
    services.graphQLClient.initialize({
      url: process.env.ADMIN_API_URL,
      token,
    });
    services.graphQLClientSocket.initialize({
      url: process.env.ADMIN_SOCKET_URL,
      token,
    });
  };

  checkPlatform = () => {
    const platform = detectPlatform();
    if (platform === 'ios') {
      services.pwa.renderIOSSplashScreen();
    }
    document.body.classList.add('platform-' + platform);
  };

  onUnreadCountChange = (count) => {
    const isStandalone = services.pwa.isRunningStandalone();
    if (!isStandalone) {
      const { base } = parseDocumentTitle();
      document.title = (count ? `(${count}) ` : '') + base;
    }
    services.pwa.setBadgeCounter(count);
  };

  onPushNotificationReceive = (rawMessage) => {
    const { currentCompany } = this.props;
    const companyId = rawMessage?.company?.id;
    if (companyId === currentCompany.id) {
      services.messageSubscription.onMessageReceive(serializeMessageFull(rawMessage));
    }
  };

  onPushNotificationClick = (targetPath) => {
    const { isLoggedIn } = this.props;
    if (isLoggedIn && targetPath) {
      browserHistory.push(targetPath);
    }
  };

  onLogoutClick = () => {
    services.pushNotifications.removeCurrentPushSubscription().finally(() => {
      services.graphQLClientSocket.destroy();
      requestAnimationFrame(services.userWatcher.logOut);
    });
  };

  onMessageReceive = message => {
    const { currentOperator, currentRoom, workHoursSchedulerIsWorking } = this.props;
    const isAppBlurred = currentOperator.hasSchedule && !workHoursSchedulerIsWorking;
    const shouldPlayNotification = !isAppBlurred && !message.sender.isBot && !message.sender.isCurrentOperator && (
      message.room.id === currentRoom.id ||
      message.room.assignedOperator.id === currentOperator.id
    );
    logInfo('App: new message', {
      message,
      isAppBlurred,
      shouldPlayNotification,
    });
    if (shouldPlayNotification) {
      services.openedTabsWatcher.runInTabThatReceivedUserGesture(() => {
        services.soundNotifications.playNotificationSound();
      });
      services.openedTabsWatcher.runInActiveTab(() => {
        services.pushNotifications.showNotification(message);
      });
    }
    services.roomListSubscription.syncRoomListOnNewMessage(message);
  };

  onToggleMuteClick = () => {
    const { isAppMuted } = this.state;
    services.soundNotifications.toggleMute({ isMuted: !isAppMuted });
  };

  onSchedulerChange = () => {
    const isEnabled = !this.state.isSchedulerEnabled;
    const isGoingToTakeUnscheduledBreak = services.workHoursScheduler.detectTakingUnscheduledBreak(isEnabled);
    if (isGoingToTakeUnscheduledBreak) {
      this.setState({
        isSchedulerReasonModalOpen: true,
        isSchedulerEnabled: false,
      });
    }
    else {
      this.setState({ isSchedulerReasonModalOpen: false });
      services.workHoursScheduler.switchManually({ isWorking: isEnabled });
    }
  };

  onSchedulerReasonModalSuccess = () => {
    this.setState({ isSchedulerReasonModalOpen: false });
  };

  onSchedulerReasonModalClose = () => {
    const { workHoursSchedulerIsWorking } = this.props;
    this.setState({
      isSchedulerReasonModalOpen: false,
      isSchedulerEnabled: workHoursSchedulerIsWorking,
    });
  };

  render(){
    const { newVersionAvailable, isLoggedIn } = this.props;
    const {
      isLoading,
      isAppMuted,
      isEssentialDataMissing,
      isSchedulerEnabled,
      isSchedulerReasonModalOpen,
    } = this.state;

    if (isEssentialDataMissing) {
      return (
        <div className={styles.dataMissingMessage}>
          <div>
            <h1>Данные с сервера не получены</h1>
            <p>Такая ошибка могла произойти, если:</p>
            <ul>
              <li>У вас плохое соединение с интернетом</li>
              <li>Доступ к серверу заблокирован</li>
              <li>На сервере произошла ошибка</li>
              <li>Сервер настроен не до конца</li>
            </ul>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <VersionWatcher/>
        <Alerts className={styles.alerts}/>

        {isLoading && (
          <LoadingScreen/>
        )}

        <Header className={cn({
          [styles.header]: true,
          [styles.headerNewVersionAvailable]: newVersionAvailable,
        })}
          isAppMuted={isAppMuted}
          isSchedulerEnabled={isSchedulerEnabled}
          onSchedulerChange={this.onSchedulerChange}
          onToggleMuteClick={this.onToggleMuteClick}
          onLogoutClick={this.onLogoutClick}
        />

        {(isLoggedIn && isSchedulerReasonModalOpen) && (
          <SchedulerReasonModal
            className={styles.schedulerReasonModal}
            onSuccess={this.onSchedulerReasonModalSuccess}
            onClose={this.onSchedulerReasonModalClose}/>
        )}

        <Switch>
          <Route exact path="/" component={PageChat}/>
          <Route exact path="/company/:companyId" component={PageChat}/>
          <Route exact path="/company/:companyId/room/:roomId" component={PageChat}/>
          <Route exact path="/profile" component={PageProfile}/>
          <Route exact path="/schedule" component={PageSchedule}/>
          <Route exact path="/schedule/:operatorId" component={PageSchedule}/>
          <Route exact path="/schedule/:operatorId/:monthName" component={PageSchedule}/>
          <Route exact path="/schedule/:operatorId/:monthName/:isEditing" component={PageSchedule}/>
          <Route exact path="/analytics" component={PageAnalytics}/>
          <Route exact path="/analytics/:reportType" component={PageAnalytics}/>
          <Route exact path="/issues" component={PageIssues}/>
          <Route exact path="/issues/:issueId" component={PageIssues}/>
          <Route path="/login" component={PageLogin} />
          <Route component={PageNotFound}/>
        </Switch>
      </div>
    );
  }
}
